import { Component, Input, OnChanges, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { RbSwitchChange } from '@inst-iot/bosch-angular-ui-components';
import { FleetGeneralFigureService } from 'src/app/_services/fleet-general-figure.service';
import { FleetGeneralInsightsService } from 'src/app/_services/fleet-general-insights.service';
import { DatePipe } from '@angular/common';
import {
  BatteryTypeOption,
  toBatteryTypeFilter,
  toBatteryTypeSlug
} from 'src/app/_shared/utils/battery-type';

/** One sub-tab per battery type: the components below cannot be compared within a single chart. */
export interface FleetBatteryTypeTab extends BatteryTypeOption {
  /** DOM-safe suffix, used to keep the Plotly element ids unique across tabs. */
  slug: string;
  /** Value sent to the APIs: always the battery type of the tab, `all_battery_types` included. */
  filter: string[];
  fleetStatus: any[];
  batteryConditions: any;
  dataLoadingFleetStatus: boolean;
  errorMessageFleetStatus: boolean;
  dataLoadingBCEvents: boolean;
  errorMessageBCEvents: boolean;
}

@Component({
  selector: 'app-general-fleet',
  templateUrl: './general-fleet.component.html',
  styleUrls: ['./general-fleet.component.scss']
})
export class GeneralFleetComponent implements OnChanges, OnDestroy {
  private readonly unsubscribe$ = new Subject<void>();
  @Input() fleetId: string[] = [];
  @Input() selectedBatteryTypes: BatteryTypeOption[] = [];
  @Input() companyData: any;
  @Input() isMetric: boolean | undefined;
  splitStrings: string[] = [];


  /** One tab per selected battery type. */
  dynamicTabItems: FleetBatteryTypeTab[] = [];
  /** Battery types forwarded to the comparison charts rendered outside the sub-tabs. */
  batteryTypeFilter: string[] = [];

  // Switch between Current and Predicted SOHC
  switchSoh!: boolean;
  switch = {
    checked: false,
    disabled: false
  };

  constructor(
    private readonly fleetGeneralInsightsService: FleetGeneralInsightsService,
    private readonly fleetGeneralFigureService: FleetGeneralFigureService,
    private readonly datePipe: DatePipe
  ) {}
  
  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  ngOnChanges(): void {
    this.batteryTypeFilter = toBatteryTypeFilter(this.selectedBatteryTypes);
    this.dynamicTabItems = this.buildTabs();
    this.dynamicTabItems.forEach((tab) => {
      this.fetchFleetStatus(tab);
      this.fetchBatteryCondition(tab);
    });
  }

  private buildTabs(): FleetBatteryTypeTab[] {
    return this.selectedBatteryTypes
      .filter((option) => option?.batteryType)
      .map((option) => ({
        ...option,
        slug: toBatteryTypeSlug(option.batteryType),
        filter: [option.batteryType],
        fleetStatus: [],
        batteryConditions: {},
        dataLoadingFleetStatus: false,
        errorMessageFleetStatus: false,
        dataLoadingBCEvents: false,
        errorMessageBCEvents: false
      }));
  }

  /** One call per battery type tab: each tab only requests its own battery type. */
  private fetchFleetStatus(tab: FleetBatteryTypeTab): void {
    tab.dataLoadingFleetStatus = true;
    tab.errorMessageFleetStatus = false;
    this.fleetGeneralInsightsService.getFleetStatus(this.fleetId, tab.filter)
      .pipe(takeUntil(this.unsubscribe$)).subscribe({
        next: (fleetInfo: any) => {
          const entries = Object.keys(fleetInfo ?? {}).flatMap((fleetId: string) => fleetInfo[fleetId] ?? []);
          const validEntries = entries.filter((entry: any) => entry?.statusCode != null && entry?.statusText != null);
          if (validEntries.length === 0) {
            tab.fleetStatus = [];
            tab.errorMessageFleetStatus = true;
            return;
          }
          validEntries.forEach((entry: any) => {
            entry.splittedText = (entry.statusText ?? '').split('-').map((str: string) => str.trim());
          });
          tab.fleetStatus = validEntries;
        },
        error: (error) => {
          console.error('Error fetching Fleet Status data:', error);
          tab.dataLoadingFleetStatus = false;
          tab.errorMessageFleetStatus = true;
        },
        complete: () => {
          tab.dataLoadingFleetStatus = false;
        }
      });
  }

  /**
   * The battery condition response is keyed by fleet only, so it cannot be split client side:
   * one call per battery type tab is required.
   */
  private fetchBatteryCondition(tab: FleetBatteryTypeTab): void {
    tab.dataLoadingBCEvents = true;
    tab.errorMessageBCEvents = false;
    this.fleetGeneralFigureService.getBatteryConditionFleet(this.fleetId, tab.filter)
      .pipe(takeUntil(this.unsubscribe$)).subscribe({
        next: (datapoints) => {
          tab.batteryConditions = datapoints;
        },
        error: (error) => {
          console.error('Error fetching Battery Condition Fleet data:', error);
          tab.dataLoadingBCEvents = false;
          tab.errorMessageBCEvents = true;
        },
        complete: () => {
          tab.dataLoadingBCEvents = false;
        }
      });
  }

  switchChange($event: RbSwitchChange) {
    this.switchSoh = $event.checked
  }

  formatDateWithNbsp(date: string | Date | null | undefined): string {
    if (!date) return 'N/A';
    const formatted = this.datePipe.transform(date, 'dd MMM yyyy');
    return formatted ? formatted.replace(/ /g, '&nbsp;') : 'N/A';
  }
}
